import React, { useState, useEffect } from 'react';
import { Card, Select, Radio, Spin, Empty, Space, Typography } from 'antd';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import dayjs from 'dayjs';
import { shippingAPI, RateTrendsResponse, RateTrendChannel } from '../../../services/shippingService';

const { Option } = Select;
const { Title } = Typography;

export const ShippingRateTrendChart: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<RateTrendsResponse | null>(null);
    const [months, setMonths] = useState<number>(6);
    const [weightRange, setWeightRange] = useState<string>('0-20');
    const [destination, setDestination] = useState<string>('美国');

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await shippingAPI.getRateTrends({
                destination,
                months,
                weight_range: weightRange
            });
            setData(response.data);
        } catch (error) {
            console.error('Failed to fetch rate trends:', error);
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchData();
    }, [months, weightRange, destination]);

    // Transform data for Recharts
    // Recharts expects an array of objects, where each object represents a time point (X-axis)
    // and keys represent different lines (Y-axis values).
    const transformData = (trends: RateTrendChannel[]) => {
        if (!trends || trends.length === 0) return [];

        // Collect all unique dates
        const dateMap = new Map<string, any>();

        trends.forEach(channel => {
            channel.data.forEach(point => {
                const dateStr = point.date;
                if (!dateMap.has(dateStr)) {
                    dateMap.set(dateStr, { date: dateStr });
                }
                const current = dateMap.get(dateStr);
                // Use channel name as key
                current[channel.channel_name] = point.price_per_kg;
            });
        });

        return Array.from(dateMap.values()).sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());
    };

    const chartData = data ? transformData(data.trends) : [];

    // Generate colors for lines
    const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#2f54eb'];

    return (
        <Card
            title={<Title level={5} style={{ margin: 0, fontSize: '14px' }}>物流运费趋势</Title>}
            style={{ marginBottom: 16 }}
            size="small"
            bodyStyle={{ padding: '12px 12px 0 12px' }}
            extra={
                <Space size={8}>
                    <Select
                        value={destination}
                        onChange={setDestination}
                        style={{ width: 90 }}
                        size="small"
                        bordered={false}
                    >
                        <Option value="美国">美国</Option>
                        <Option value="加拿大">加拿大</Option>
                        <Option value="英国">英国</Option>
                        <Option value="德国">德国</Option>
                    </Select>
                    <Select
                        value={weightRange}
                        onChange={setWeightRange}
                        style={{ width: 100 }}
                        size="small"
                        bordered={false}
                    >
                        <Option value="0-20">0-20kg</Option>
                        <Option value="20-100">20-100kg</Option>
                        <Option value="100+">100kg+</Option>
                    </Select>
                    <Radio.Group
                        value={months}
                        onChange={e => setMonths(e.target.value)}
                        size="small"
                        buttonStyle="solid"
                    >
                        <Radio.Button value={3}>3月</Radio.Button>
                        <Radio.Button value={6}>6月</Radio.Button>
                        <Radio.Button value={12}>1年</Radio.Button>
                    </Radio.Group>
                </Space>
            }
        >
            <Spin spinning={loading}>
                {chartData.length > 0 ? (
                    <div style={{ width: '100%', height: 220 }}>
                        <ResponsiveContainer>
                            <LineChart
                                data={chartData}
                                margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={(str) => dayjs(str).format('MM/DD')}
                                    tick={{ fontSize: 11, fill: '#999' }}
                                    tickLine={false}
                                    axisLine={{ stroke: '#f0f0f0' }}
                                    dy={5}
                                />
                                <YAxis
                                    tick={{ fontSize: 11, fill: '#999' }}
                                    tickLine={false}
                                    axisLine={false}
                                    width={35}
                                />
                                <Tooltip
                                    labelFormatter={(label) => dayjs(label).format('YYYY-MM-DD')}
                                    formatter={(value: number) => [`¥${value}`, '']}
                                    contentStyle={{ fontSize: 12, padding: '4px 8px' }}
                                />
                                <Legend
                                    wrapperStyle={{ paddingTop: '5px', fontSize: '12px' }}
                                    iconSize={8}
                                />
                                {data?.trends.map((channel, index) => (
                                    <Line
                                        key={channel.channel_id}
                                        type="monotone"
                                        dataKey={channel.channel_name}
                                        stroke={colors[index % colors.length]}
                                        dot={false}
                                        activeDot={{ r: 4 }}
                                        strokeWidth={1.5}
                                        name={channel.channel_name}
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" style={{ margin: '20px 0' }} />
                )}
            </Spin>
        </Card>
    );
};
